// Pure helpers for detecting concurrent edits to a BrandItem before saving.
//
// The BrandItemUpdate mutation is a blind last-write-wins overwrite, so a user
// who saves an item that someone (or an integration) changed after they opened
// it would silently clobber those changes. These helpers compare the loaded
// `updatedAt` against the server's current value and translate the object
// patches written in between into a readable, editor-relevant summary.
//
// They are intentionally free of I/O so they can be unit tested in isolation.

/** A single JSON-patch operation as returned by the BrandItemConflictCheck query. */
export type ConflictPatchOp = {
   path?: string | null;
};

/** A patch (set of ops) recorded for one write to the item. */
export type ConflictPatch = {
   date: Date | string;
   updatedBy?: { ref?: string | null; type: string } | null;
   ops?: (ConflictPatchOp | null)[] | null;
};

/** Who/what performed a write, as carried on BrandItem.updatedBy / a patch. */
export type ConflictUpdatedBy = { ref?: string | null; type: string } | null | undefined;

export type ConflictResult = {
   /** Editor-managed field labels that changed since load (deduped, in display order). */
   changedFields: string[];
   /** True when the change was made by a human user (ref present), false for automated writes. */
   byUser: boolean;
   /** True when we know the item changed but could not load patch detail to describe it. */
   detailUnavailable: boolean;
};

// Maps the root segment of a patch path to a human-readable label, limited to the
// fields the BrandItem editor can actually change. Paths not listed here are
// treated as system/automated changes (e.g. salesScore, image processing,
// stock-location timestamps) and ignored, so they never trigger a false conflict.
export const EDITOR_FIELD_LABELS: Record<string, string> = {
   nameLocalized: "Name",
   descriptionLocalized: "Description",
   colorLocalized: "Color",
   sizeLocalized: "Size",
   typeLocalized: "Type",
   commentLocalized: "Comment",
   commentApproved: "Comment approved",
   directPrices: "Direct price",
   sellingPrices: "Selling price",
   beforePrices: "Before price",
   condition: "Condition",
   itemState: "Item state",
   stockLocation: "Stock location",
   stockLocationBag: "Stock location bag",
   checked: "Checked",
   missing: "Missing",
   unavailable: "Unavailable",
   discarded: "Discarded",
   vendor: "Vendor",
   originalEan: "Original EAN",
   originalSku: "Original SKU",
   commodityCode: "Commodity code",
   countryOrigin: "Country of origin",
   nfcTag: "NFC tag",
   note: "Note",
   test: "Test flag",
   itemTags: "Tags",
   problems: "Problems",
   options: "Options",
   images: "Images",
   sortorder: "Sort order",
   disableRRPListPrice: "RRP list price setting",
   brandProductSizeId: "Linked product/variant",
};

/**
 * Extract the root field key from a patch path. Handles JSON-pointer
 * ("/sellingPrices/0/value") and dotted ("sellingPrices.0.value") forms.
 */
export const patchPathRoot = (path?: string | null): string | null => {
   if (!path) return null;
   const root = path.replace(/^\//, "").split(/[/.]/)[0];
   return root || null;
};

const toMillis = (value: Date | string | null | undefined): number =>
   value ? new Date(value).getTime() : NaN;

/**
 * Decide whether there is a concurrent-edit conflict the user must resolve.
 *
 * Returns null when it is safe to save without prompting, i.e. when the item is
 * unchanged since load, or when only non-editor (system/automated) fields changed.
 */
export const evaluateConflict = (
   loadedUpdatedAt: Date | string | null | undefined,
   serverUpdatedAt: Date | string | null | undefined,
   serverUpdatedBy: ConflictUpdatedBy,
   patches: (ConflictPatch | null)[] | null | undefined
): ConflictResult | null => {
   const loadedMs = toMillis(loadedUpdatedAt);
   const serverMs = toMillis(serverUpdatedAt);

   // Missing or unparseable timestamps: we cannot compare, so don't block saving.
   if (!Number.isFinite(loadedMs) || !Number.isFinite(serverMs)) return null;

   // Nothing changed since we loaded the item -> safe to save.
   if (serverMs <= loadedMs) return null;

   // The item changed. Keep only the patches written after we loaded it
   // (the patch filter has no date operator, so we filter client-side).
   const since = (patches || [])
      .filter((patch): patch is ConflictPatch => !!patch)
      .filter((patch) => toMillis(patch.date) > loadedMs);

   if (since.length === 0) {
      // We know it changed but have no patch detail to describe it -> warn conservatively.
      return { changedFields: [], byUser: !!serverUpdatedBy?.ref, detailUnavailable: true };
   }

   // Translate changed op paths into editor-managed field labels (deduped, ordered).
   const changedFields: string[] = [];
   for (const patch of since) {
      for (const op of patch.ops || []) {
         const root = patchPathRoot(op?.path);
         const label = root ? EDITOR_FIELD_LABELS[root] : undefined;
         if (label && !changedFields.includes(label)) changedFields.push(label);
      }
   }

   // Only non-editor/system fields changed -> nothing the user could have edited -> safe to save.
   if (changedFields.length === 0) return null;

   return {
      changedFields,
      byUser: since.some((patch) => !!patch.updatedBy?.ref),
      detailUnavailable: false,
   };
};

// Build the readable body shown in the conflict confirmation dialog. Each line
// is rendered as a separate paragraph keyed by its text, so the lines are kept
// distinct (no blank spacer lines, which would collide on an empty React key).
export const buildConflictMessage = (conflict: ConflictResult): string => {
   const who = conflict.byUser ? "another user" : "an automated process";

   if (conflict.detailUnavailable) {
      return [
         `This item was changed on the server by ${who} after you opened it.`,
         `The details of that change could not be loaded.`,
         `Choose "Overwrite" to save your version anyway, or "Cancel" to keep editing — reload the page to get the latest version.`,
      ].join("\n");
   }

   return [
      `This item was changed by ${who} after you opened it.`,
      `Changed since you opened it:`,
      ...conflict.changedFields.map((field) => `• ${field}`),
      `Choose "Overwrite" to save your version over these changes, or "Cancel" to keep your edits — reload the page to get the latest version.`,
   ].join("\n");
};
